import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from "recharts";
import moment from "moment";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    getImage,
    getSearchedUsers,
    getSubscriberList,
    getUserProfile,
} from "../../store/user/user.selector";
import { getOrderList } from "../../store/shopping/shopping.selector";
import { useEffect } from "react";
import { fetchSubscriberListRequest } from "../../store/user/user.actions";
import ShoppingAPI from "../../store/shopping/shopping.api";
import SocialAPI from "../../store/social/social.api";
import { getOrderRequest } from "../../store/shopping/shopping.actions";
import _ from "underscore";
import dashIcon1 from "../../assets/images/dashboard-1.svg";
import dashIcon2 from "../../assets/images/dashboard-2.svg";
import dashIcon3 from "../../assets/images/dashboard-3.svg";
import dashIcon4 from "../../assets/images/dashboard-4.svg";
import {
    getDaysOfMonth,
    getLastFiveYear,
    getMonthsOfYear,
} from "../../helpers/utility";

const Dashboard = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [selectedYear, setYear] = useState(moment().format("YYYY"));
    const [spendings, setSpendings] = useState([]);
    const [livestreams, setLiveStreams] = useState([]);
    const [earnings, setEarnings] = useState([]);
    const [period, setPeriod] = useState("month");
    const { userProfile, subscriberList, orders, searchUsers } = useSelector(
        (state) => {
            return {
                userProfile: getUserProfile(state),
                subscriberList: getSubscriberList(state),
                orders: getOrderList(state),
                searchUsers: getSearchedUsers(state),
            };
        }
    );

    useEffect(() => {
        dispatch(fetchSubscriberListRequest());
        dispatch(getOrderRequest());
        ShoppingAPI.GetTransactions("?receiveAll=true")
            .then((x) => {
                setEarnings(x.data);
            })
            .catch((x) => {
                setEarnings([]);
            });
        ShoppingAPI.GetTransactions("?spentAll=true")
            .then((x) => {
                setSpendings(x.data);
            })
            .catch((x) => {
                setSpendings([]);
            });
        SocialAPI.GetLiveStreamList("?where=owner:" + userProfile._id)
            .then((x) => {
                setLiveStreams(x.data);
            })
            .catch((x) => {
                setLiveStreams([]);
            });
    }, []);

    let selectedYearSpendings = spendings.filter(
        (x) =>
            moment(x.dateCreated).format("MMYYYY") === moment().format("MMYYYY")
    );
    let selectedYearEarnings = earnings.filter(
        (x) => moment(x.dateCreated).format("YYYY") === selectedYear.toString()
    );

    let subscriptionEarnings = earnings.filter(
        (x) => x.type === "subscription"
    );
    let orderEarnings = earnings.filter((x) => x.type === "order");

    let currentSubscriptionEarnings = earnings.filter(
        (x) =>
            x.type === "subscription" &&
            moment(x.dateCreated).format("MMYYYY") === moment().format("MMYYYY")
    );
    let lastSubscriptionEarnings = earnings.filter(
        (x) =>
            x.type === "subscription" &&
            moment(x.dateCreated).format("MMYYYY") ===
                moment().subtract(1, "month").format("MMYYYY")
    );

    let currentOrderEarnings = earnings.filter(
        (x) =>
            x.type === "order" &&
            moment(x.dateCreated).format("MMYYYY") === moment().format("MMYYYY")
    );
    let lastOrderEarnings = earnings.filter(
        (x) =>
            x.type === "order" &&
            moment(x.dateCreated).format("MMYYYY") ===
                moment().subtract(1, "month").format("MMYYYY")
    );

    let columns = [];
    if (period === "day") {
        columns = getDaysOfMonth(
            moment().format("YYYYY"),
            moment().format("MM")
        );
    }
    if (period === "month") {
        columns = getMonthsOfYear(moment().format("YYYY"));
    }

    if (period === "year") {
        columns = getLastFiveYear(moment().format("YYYY"));
    }

    const datum = columns.reverse().map((x) => {
        let sales = [];
        let subscriptions = [];
        if (period === "day") {
            sales = earnings.filter((y) => {
                return (
                    x.type !== "subscription" &&
                    moment(y.dateCreated).format("DD-MM-YYYY") ===
                        x.format("DD-MM-YYYY")
                );
            });

            subscriptions = earnings.filter((y) => {
                return (
                    x.type === "subscription" &&
                    moment(y.dateCreated).format("DD-MM-YYYY") ===
                        x.format("DD-MM-YYYY")
                );
            });
        }
        if (period === "month") {
            sales = earnings.filter((y) => {
                return (
                    x.type !== "subscription" &&
                    moment(y.dateCreated).format("MM-YYYY") ===
                        x.format("MM-YYYY")
                );
            });

            subscriptions = earnings.filter((y) => {
                return (
                    x.type === "subscription" &&
                    moment(y.dateCreated).format("MM-YYYY") ===
                        x.format("MM-YYYY")
                );
            });
        }
        if (period === "year") {
            sales = earnings.filter((y) => {
                return (
                    x.type !== "subscription" &&
                    moment(y.dateCreated).format("YYYY") === x.format("YYYY")
                );
            });

            subscriptions = earnings.filter((y) => {
                return (
                    x.type === "subscription" &&
                    moment(y.dateCreated).format("YYYY") === x.format("YYYY")
                );
            });
        }
        return {
            name:
                period === "day"
                    ? x.format("DD-MM-YYYY")
                    : period === "month"
                    ? x.format("MM-YYYY")
                    : x.format("YYYY"),
            orderAmount: sales
                .map((y) => y.totalAmount)
                .reduce((partialSum, a) => partialSum + a, 0),
            subscriptionAmount: subscriptions
                .map((y) => y.totalAmount)
                .reduce((partialSum, a) => partialSum + a, 0),
            amt: x.netAmount,
        };
    });

    let dateStart = moment("01/01/" + selectedYear).startOf("year");
    let dateEnd = moment();
    let timeValues = [];

    while (
        dateEnd > dateStart ||
        dateStart.format("M") === dateEnd.format("M")
    ) {
        timeValues.push(dateStart.format("MM"));
        dateStart.add(1, "month");
    }

    timeValues = _.uniq(timeValues);

    const revenues = timeValues.map((x) => {
        let xy = moment(x + "/01/" + selectedYear).format("MMYYYY");
        let yx = moment(x + "/01/" + selectedYear)
            .subtract(1, "month")
            .format("MMYYYY");
        let yx1 = moment(x + "/01/" + selectedYear)
            .subtract(2, "month")
            .format("MMYYYY");
        return {
            month: moment(x + "/01/" + selectedYear).format("MMMM"),
            year: moment(x + "/01/" + selectedYear).format("YYYY"),
            earnings: earnings
                .filter((x) => moment(x.dateCreated).format("MMYYYY") === xy)
                .map((x) => x.netAmount)
                .reduce((partial_sum, a) => partial_sum + a, 0),
            lastEarnings: earnings
                .filter((x) => moment(x.dateCreated).format("MMYYYY") === yx)
                .map((x) => x.netAmount)
                .reduce((partial_sum, a) => partial_sum + a, 0),
            lastBeforeEarnings: earnings
                .filter((x) => moment(x.dateCreated).format("MMYYYY") === yx1)
                .map((x) => x.netAmount)
                .reduce((partial_sum, a) => partial_sum + a, 0),
        };
    });

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="custom-tooltip p-4 rounded-lg bg-white shadow-md text-center">
                    <p className="text-[#7C8DB5] text-md">15 Aug 2022</p>
                    <p className="text-[17px] text-black">
                        ${` ${payload[0].value}`}
                    </p>
                </div>
            );
        }

        return null;
    };

    const totalEarnings = revenues.map((x, index) => {
        return {
            id: index,
            month: x.month,
            year: x.year,
            earn: x.earnings,
        };
    });

    const earningsDetails = [
        {
            id: 0,
            title: "All spendings",
            price:
                userProfile.country.currency +
                " " +
                spendings
                    .map((x) => x.totalAmount)
                    .reduce((partial_sum, a) => partial_sum + a, 0)
                    .toFixed(2),
            details: "See more",
            detailsPage: "/page/dashboard/spendings",
            img: dashIcon1,
        },
        {
            id: 1,
            title: "All earnings",
            price:
                userProfile.country.currency +
                " " +
                earnings
                    .map((x) => x.netAmount)
                    .reduce((partial_sum, a) => partial_sum + a, 0)
                    .toFixed(2),
            details: "See more",
            detailsPage: "/page/dashboard/earnings",
            img: dashIcon2,
        },
        {
            id: 2,
            title: "Subscriptions",
            price:
                userProfile.country.currency +
                " " +
                subscriptionEarnings
                    .map((x) => x.netAmount)
                    .reduce((partial_sum, a) => partial_sum + a, 0)
                    .toFixed(2),
            details: "",
            detailsPage: "",
            img: dashIcon3,
        },
        {
            id: 3,
            title: "All purchase",
            price:
                userProfile.country.currency +
                " " +
                selectedYearSpendings
                    .map((x) => x.netAmount)
                    .reduce((partial_sum, a) => partial_sum + a, 0)
                    .toFixed(2),
            details: "",
            detailsPage: "",
            img: dashIcon4,
        },
    ];

    const newSubscriber = subscriberList.map((item, index) => {
        return {
            id: index,
            img: getImage(item.subscriber.avatar),
            name: "From " + item.subscriber.fullName,
            date: "Since " + moment(item.dateCreated).fromNow(),
        };
    });

    const orderList = [
        {
            id: 0,
            type: "Subscription revenue",
            total:
                userProfile.country.currency +
                " " +
                subscriptionEarnings
                    .map((x) => x.netAmount)
                    .reduce((partial_sum, a) => partial_sum + a, 0)
                    .toFixed(2),
            lastMonth:
                userProfile.country.currency +
                " " +
                lastSubscriptionEarnings
                    .map((x) => x.netAmount)
                    .reduce((partial_sum, a) => partial_sum + a, 0)
                    .toFixed(2),
            thisMonth:
                userProfile.country.currency +
                " " +
                currentSubscriptionEarnings
                    .map((x) => x.netAmount)
                    .reduce((partial_sum, a) => partial_sum + a, 0)
                    .toFixed(2),
        },
        {
            id: 1,
            type: "Total Sales",
            total:
                userProfile.country.currency +
                " " +
                orderEarnings
                    .map((x) => x.netAmount)
                    .reduce((partial_sum, a) => partial_sum + a, 0)
                    .toFixed(2),
            lastMonth:
                userProfile.country.currency +
                " " +
                lastOrderEarnings
                    .map((x) => x.netAmount)
                    .reduce((partial_sum, a) => partial_sum + a, 0)
                    .toFixed(2),
            thisMonth:
                userProfile.country.currency +
                " " +
                currentOrderEarnings
                    .map((x) => x.netAmount)
                    .reduce((partial_sum, a) => partial_sum + a, 0)
                    .toFixed(2),
        },
        {
            id: 2,
            type: "Subscribers",
            total: subscriptionEarnings.length,
            lastMonth: lastSubscriptionEarnings.length,
            thisMonth: currentSubscriptionEarnings.length,
        },
        {
            id: 3,
            type: "Total views",
            total: livestreams
                .map((x) => x.totalWatch)
                .reduce((partial_sum, a) => partial_sum + a, 0),
            lastMonth: livestreams
                .filter(
                    (x) =>
                        moment(x.dateCreated).format("MMYYYY") ===
                        moment().subtract(1, "month").format("MMYYYY")
                )
                .map((x) => x.totalWatch)
                .reduce((partial_sum, a) => partial_sum + a, 0),
            thisMonth: livestreams
                .filter(
                    (x) =>
                        moment(x.dateCreated).format("MMYYYY") ===
                        moment().format("MMYYYY")
                )
                .map((x) => x.totalWatch)
                .reduce((partial_sum, a) => partial_sum + a, 0),
        },
    ];
    //
    const manageOrder = orders
        .filter(
            (x) =>
                moment(x.dateCreated).format("MMYYYY") ===
                moment().format("MMYYYY")
        )
        .map((order, index) => {
            let buyer = _.find(searchUsers, (x) => order.buyer._id === x._id);
            if (!buyer) {
                buyer = _.find(searchUsers, (x) => order.buyer === x._id);
            }
            return {
                id: index,
                userImg: !order.isBuyer
                    ? getImage(buyer.attachments)
                    : getImage(order.seller.attachments),
                userName: !order.isBuyer
                    ? order.buyerUserName
                    : order.sellerUserName,
                userId: order.code,
                itemImg:
                    order.firstProduct.product &&
                    order.firstProduct.product.images
                        ? order.firstProduct.product &&
                          order.firstProduct.product.images
                        : getImage({}),
                itemName: order.firstProduct && order.firstProduct.productName,
                itemId:
                    order.firstProduct.product &&
                    order.firstProduct.product.code,
                totalOrder: order.firstProduct && order.firstProduct.quantity,
                totalPrice: order.totalPrices.toFixed(2),
                trackingNo:
                    order.shippingInfo && order.shippingInfo.trackingNumber,
                status:
                    order.orderStatus === "ToShip"
                        ? "To ship"
                        : order.orderStatus === "ToReceive"
                        ? "To receive"
                        : order.orderStatus === "ToReceived"
                        ? "To receive"
                        : order.orderStatus,
            };
        });

    const data = datum.map((x) => {
        return {
            month: x.name,
            orderAmount: x.orderAmount,
            subscriptionAmount: x.subscriptionAmount,
        };
    });

    return (
        <div className="p-2 sm:p-4 lg:px-8 lg:py-7 ">
            <div className="mb-[25px]">
                <h2 className="text-xl font-bold text-ebony-color">
                    Dashboard
                </h2>
                <p className="text-md font-medium text-black-50">
                    Track your spending, earnings, subscription and revenue.
                </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                {earningsDetails.map((details) => (
                    <div
                        key={details.id}
                        className="p-4 bg-white rounded-lg border border-black-10"
                    >
                        <div className="flex items-center justify-between mb-2">
                            <div className="">
                                <h4 className="text-md font-medium text-black-50 mb-1.5">
                                    {details.title}
                                </h4>
                                <p className="text-xl font-bold text-ebony-color">
                                    {details.price}
                                </p>
                            </div>
                            <img src={details.img} alt="" />
                        </div>
                        <Link
                            to={details.detailsPage}
                            className="text-md font-medium text-orange-color"
                        >
                            {details.details}
                        </Link>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-12 gap-4 mb-4">
                <div className="col-span-12 md:col-span-6 lg:col-span-9 bg-white rounded-lg border border-black-10 p-[22px] pb-3.5">
                    <div className="flex items-center justify-between mb-3">
                        <h5 className="text-md sm:text-md font-medium text-black-50">
                            Revenue
                        </h5>

                        <div className="text-md font-medium flex items-center gap-2.5 text-black sm:gap-x-5">
                            <div className=" flex items-center">
                                <span className="inline-block w-2 h-2 rounded-full bg-[#347AE2] mr-2"></span>
                                <span className="whitespace-nowrap">Sales</span>
                            </div>
                            <div className=" flex items-center">
                                <span className="inline-block w-2 h-2 rounded-full bg-orange-color mr-2"></span>
                                <span className="whitespace-nowrap">
                                    Subscription{" "}
                                </span>
                            </div>
                            <div className="">
                                <select
                                    name=""
                                    id=""
                                    value={period}
                                    onChange={(e) => setPeriod(e.target.value)}
                                    className="w-[80px] sm:w-[100px] rounded-lg outline-none ring-1 text-black-50 ring-black-10 bg-white py-[7px] pl-2 sm:pl-3.5 border-r-[8px] sm:border-r-[10px] border-transparent capitalize"
                                >
                                    <option value="year">Yearly</option>
                                    <option value="month">Monthly</option>
                                    <option value="day">Daily</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <ResponsiveContainer width="100%" height="90%">
                        <LineChart
                            width={500}
                            data={data}
                            margin={{
                                top: 5,
                                right: 30,
                                left: 20,
                                bottom: 5,
                            }}
                        >
                            <CartesianGrid
                                horizontal="true"
                                vertical=""
                                stroke="rgba(0, 0, 0, 0.1)"
                                strokeWidth={1}
                            />
                            <XAxis
                                tickMargin="15"
                                dataKey="month"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fill: "rgba(0, 0, 0, 0.5)" }}
                            />
                            <YAxis
                                tickMargin="15"
                                dataKey=""
                                axisLine={false}
                                tickLine={false}
                                type="number"
                                domain={["auto", "auto"]}
                                tick={{ fill: "rgba(0, 0, 0, 0.5)" }}
                            />
                            <Tooltip
                                content={<CustomTooltip />}
                                wrapperStyle={{
                                    border: "none",
                                    outline: "none",
                                }}
                            />
                            <Line
                                type="monotone"
                                dot={false}
                                dataKey="orderAmount"
                                stroke="#FF9500"
                                strokeWidth={2}
                                activeDot={{
                                    fill: "#347AE2",
                                    strokeWidth: 0,
                                    r: 4,
                                }}
                            />
                            <Line
                                type="monotone"
                                dataKey="subscriptionAmount"
                                dot={false}
                                stroke="#7900F8"
                                strokeWidth={2}
                                activeDot={{
                                    fill: "#347AE2",
                                    strokeWidth: 0,
                                    r: 4,
                                }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
                <div className="col-span-12 md:col-span-6 lg:col-span-3 bg-white rounded-lg border border-black-10 pb-1">
                    <div className="p-4 pb-0 mb-0.5">
                        <h3 className="text-md font-medium text-black-50">
                            New subscribers
                        </h3>
                    </div>
                    <div className="p-2.5 pt-0">
                        {newSubscriber.map((subscriber) => (
                            <div
                                key={subscriber.id}
                                className="flex items-center justify-start gap-4 p-2"
                            >
                                <img
                                    src={subscriber.img}
                                    alt=""
                                    className="w-12 h-12 rounded-full"
                                />
                                <div className="">
                                    <h4 className="text-md font-medium text-black mb-0.5">
                                        {subscriber.name}
                                    </h4>
                                    <p className="text-md font-normal text-[#A1A1A1]">
                                        {subscriber.date}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Order list  */}

            <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 md:col-span-6 lg:col-span-9">
                    <div className=" bg-white rounded-lg border border-black-10 p-4 mb-4">
                        <div className="mb-2.5">
                            <h3 className="text-md font-medium text-black-50 capitalize">
                                Order List
                            </h3>
                        </div>

                        <div className="overflow-x-auto relative px-1.5">
                            <table className="w-full text-left pl-2.5">
                                <thead className="text-md font-medium text-black-50 capitalize border-b border-black-10 pb-[7px]">
                                    <tr>
                                        <th
                                            scope="col"
                                            className="py-3 px-6 whitespace-nowrap"
                                        >
                                            type
                                        </th>
                                        <th
                                            scope="col"
                                            className="py-3 px-6 whitespace-nowrap"
                                        >
                                            total
                                        </th>
                                        <th
                                            scope="col"
                                            className="py-3 px-6 whitespace-nowrap"
                                        >
                                            last month
                                        </th>
                                        <th
                                            scope="col"
                                            className="py-3 px-6 whitespace-nowrap"
                                        >
                                            this month
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="text-md text-black-60">
                                    {orderList.map((order) => (
                                        <tr
                                            key={order.id}
                                            className="bg-white border-b border-black-10 "
                                        >
                                            <td className="py-3.5 px-6 whitespace-nowrap text-black font-bold capitalize">
                                                {order.type}
                                            </td>
                                            <td className="py-3.5 px-6">
                                                {order.total}
                                            </td>
                                            <td className="py-3.5 px-6">
                                                {order.lastMonth}
                                            </td>
                                            <td className="py-3.5 px-6">
                                                {order.thisMonth}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="p-6 bg-white rounded-lg border border-black-10">
                        <h4 className="text-md font-medium text-black-50 mb-3.5">
                            Purchase this month
                        </h4>
                        {manageOrder.length > 0 ? (
                            <div className="">
                                <div className="">
                                    <div className="w-full text-left">
                                        <div className="text-md font-medium capitalize text-black-50 border-b border-black-10 hidden md:block">
                                            <tr className="grid grid-cols-8">
                                                <th
                                                    scope="col"
                                                    className="pt-[18px] pb-3.5 ppl-4 ppr-10 whitespace-nowrap col-span-2"
                                                >
                                                    name
                                                </th>
                                                <th
                                                    scope="col"
                                                    className="pt-[18px] pb-3.5 ppx-10 whitespace-nowrap col-span-2"
                                                >
                                                    item
                                                </th>
                                                <th
                                                    scope="col"
                                                    className="pt-[18px] pb-3.5 ppx-6 whitespace-nowrap"
                                                >
                                                    order total
                                                </th>
                                                <th
                                                    scope="col"
                                                    className="pt-[18px] pb-3.5 ppx-6 whitespace-nowrap "
                                                >
                                                    total
                                                </th>
                                                <th
                                                    scope="col"
                                                    className="pt-[18px] pb-3.5 ppx-6 whitespace-nowrap"
                                                >
                                                    tracking no
                                                </th>
                                                <th
                                                    scope="col"
                                                    className="pt-[18px] pb-3.5 ppx-6 whitespace-nowrap md:pl-8 lg:pl-7"
                                                >
                                                    status
                                                </th>
                                            </tr>
                                        </div>
                                        <div className="">
                                            {manageOrder
                                                .slice(0, 5)
                                                .map((order) => (
                                                    <div
                                                        key={order.id}
                                                        className="flex md:flex-none w-full py-2 md:py-0 border-b border-black-10 md:border-none"
                                                    >
                                                        <div className="flex-1 text-md font-medium capitalize text-black-50 md:hidden ">
                                                            <tr className=" grid gap-y- grid-cols-1">
                                                                <div className="py-1  whitespace-nowrap min-h-[53px] flex items-center">
                                                                    name
                                                                </div>
                                                                <div className="py-1  whitespace-nowrap min-h-[53px] flex items-center">
                                                                    item
                                                                </div>
                                                                <div className="py-1 mt-2.5 whitespace-nowrap">
                                                                    order total
                                                                </div>
                                                                <div className="py-1 whitespace-nowrap ">
                                                                    total
                                                                </div>
                                                                <div className="py-1 whitespace-nowrap">
                                                                    tracking no
                                                                </div>
                                                                <div className="py-1 whitespace-nowrap">
                                                                    status
                                                                </div>
                                                            </tr>
                                                        </div>
                                                        <tr className="flex-1 text-md text-black-60 md:border-b border-black-10 w-full grid gap-y- grid-cols-1 md:grid-cols-8 items-center">
                                                            <td className="py-1 md:py-3 ppl-3.5 ppr-10 md:col-span-2">
                                                                <div className="flex items-center">
                                                                    <img
                                                                        src={
                                                                            order.userImg
                                                                        }
                                                                        alt=""
                                                                        className="w-[45px] h-[45px] rounded-full mr-3"
                                                                    />
                                                                    <div className="">
                                                                        <h4 className="text-md font-medium text-black mb-0.5 capitalize whitespace-nowrap">
                                                                            {
                                                                                order.userName
                                                                            }
                                                                        </h4>
                                                                        <p className="">
                                                                            {
                                                                                order.userId
                                                                            }
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="py-1 md:py-3 ppx-10  md:col-span-2">
                                                                <div className=" flex items-center">
                                                                    <img
                                                                        src={
                                                                            order.itemImg
                                                                        }
                                                                        alt=""
                                                                        className="w-[45px] h-[45px] rounded-full mr-3"
                                                                    />
                                                                    <div className="">
                                                                        <h4 className="text-md font-medium text-black mb-0.5 capitalize whitespace-nowrap overflow-text max-w-[150px]">
                                                                            {
                                                                                order.itemName
                                                                            }
                                                                        </h4>
                                                                        <p className=" uppercase">
                                                                            {
                                                                                order.itemId
                                                                            }
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="py-1 md:py-3 ppx-6 md:pl-7 ">
                                                                x
                                                                {
                                                                    order.totalOrder
                                                                }
                                                            </td>
                                                            <td className="py-1 md:py-3 ppx-6 uppercase whitespace-nowrap">
                                                                myr{" "}
                                                                {
                                                                    order.totalPrice
                                                                }
                                                            </td>
                                                            <td className="py-1 md:py-3 ppx-6">
                                                                {
                                                                    order.trackingNo
                                                                }
                                                            </td>
                                                            <td className="py-1 md:py-3 ppx-6 md:pl-8 lg:pl-7">
                                                                <Link
                                                                    to=""
                                                                    className={`${
                                                                        order.status.toLowerCase() ===
                                                                        "to ship"
                                                                            ? "text-orange-color"
                                                                            : "text-[#168A2F]"
                                                                    } capitalize hover:underline`}
                                                                >
                                                                    {
                                                                        order.status
                                                                    }
                                                                </Link>
                                                            </td>
                                                        </tr>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center justify-between">
                                <div className="flex items-center justify-start">
                                    <span className="inline-block mr-2.5">
                                        <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 14 14"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M6.5625 0C2.93848 0 0 2.93848 0 6.5625C0 10.1865 2.93848 13.125 6.5625 13.125C10.1865 13.125 13.125 10.1865 13.125 6.5625C13.125 2.93848 10.1865 0 6.5625 0ZM7.03125 9.72656C7.03125 9.79102 6.97852 9.84375 6.91406 9.84375H6.21094C6.14648 9.84375 6.09375 9.79102 6.09375 9.72656V5.74219C6.09375 5.67773 6.14648 5.625 6.21094 5.625H6.91406C6.97852 5.625 7.03125 5.67773 7.03125 5.74219V9.72656ZM6.5625 4.6875C6.3785 4.68374 6.20331 4.60801 6.07452 4.47656C5.94572 4.34511 5.87358 4.16841 5.87358 3.98438C5.87358 3.80034 5.94572 3.62364 6.07452 3.49219C6.20331 3.36074 6.3785 3.28501 6.5625 3.28125C6.7465 3.28501 6.92169 3.36074 7.05048 3.49219C7.17928 3.62364 7.25142 3.80034 7.25142 3.98438C7.25142 4.16841 7.17928 4.34511 7.05048 4.47656C6.92169 4.60801 6.7465 4.68374 6.5625 4.6875Z"
                                                fill="#FF0000"
                                            />
                                        </svg>
                                    </span>
                                    <p className="text-md text-[#FF0000]">
                                        No products purchased this month
                                    </p>
                                </div>
                                <button
                                    onClick={() => navigate("/page/order")}
                                    className="text-md font-medium text-orange-color capitalize"
                                >
                                    see more
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                <div className="col-span-12 md:col-span-6 lg:col-span-3 bg-white rounded-lg border border-black-10 p-4">
                    <h4 className="text-md font-medium text-black-50 mb-3">
                        Total earnings
                    </h4>

                    <div className="flex justify-between items-center mb-2">
                        <button
                            className="py-[7px] px-2 rounded-full shadow-[0_1px_4px] shadow-black-5"
                            style={{
                                opacity:
                                    parseInt(
                                        moment(userProfile.dateCreated).format(
                                            "YYYY"
                                        )
                                    ) < parseInt(selectedYear)
                                        ? 1
                                        : 0.5,
                            }}
                            onClick={() => {
                                if (
                                    parseInt(
                                        moment(userProfile.dateCreated).format(
                                            "YYYY"
                                        )
                                    ) < parseInt(selectedYear)
                                ) {
                                    setYear(parseInt(selectedYear, 0) - 1);
                                }
                            }}
                        >
                            <svg
                                width="4"
                                height="7"
                                viewBox="0 0 4 7"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M3.82235 6.81584C3.87867 6.75784 3.92334 6.68895 3.95383 6.61311C3.98431 6.53727 4 6.45597 4 6.37386C4 6.29175 3.98431 6.21045 3.95383 6.13461C3.92334 6.05877 3.87867 5.98988 3.82235 5.93189L1.46544 3.49945L3.82235 1.06702C3.93593 0.949803 3.99974 0.790821 3.99974 0.625048C3.99974 0.459275 3.93593 0.30029 3.82235 0.183071C3.70878 0.0658521 3.55473 -3.8927e-08 3.3941 -5.29694e-08C3.23348 -6.70118e-08 3.07943 0.0658521 2.96585 0.183071L0.177645 3.06061C0.121332 3.11861 0.0766557 3.1875 0.0461729 3.26334C0.0156902 3.33918 3.12931e-07 3.42048 3.05753e-07 3.50259C2.98575e-07 3.58469 0.0156901 3.66599 0.0461729 3.74183C0.0766557 3.81767 0.121332 3.88657 0.177645 3.94456L2.96585 6.82211C3.19668 7.06033 3.58545 7.06033 3.82235 6.81584Z"
                                    fill="#2B1C50"
                                />
                            </svg>
                        </button>

                        {/* date  */}
                        <div className="">
                            <p className="text-md font-bold text-ebony-color">
                                {selectedYear}
                            </p>
                        </div>

                        <button
                            className="py-[7px] px-2 rounded-full shadow-[0_1px_4px] shadow-black-5"
                            style={{
                                opacity:
                                    parseInt(moment().format("YYYY")) >
                                    parseInt(selectedYear)
                                        ? 1
                                        : 0.5,
                            }}
                            onClick={() => {
                                if (
                                    parseInt(moment().format("YYYY")) >
                                    parseInt(selectedYear)
                                ) {
                                    setYear(parseInt(selectedYear, 0) + 1);
                                }
                            }}
                        >
                            <svg
                                width="4"
                                height="7"
                                viewBox="0 0 4 7"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M0.177646 0.184164C0.121333 0.242162 0.0766562 0.311053 0.0461734 0.386893C0.0156906 0.462733 0 0.544033 0 0.626139C0 0.708245 0.0156906 0.789545 0.0461734 0.865385C0.0766562 0.941225 0.121333 1.01012 0.177646 1.06811L2.53456 3.50055L0.177646 5.93298C0.0640659 6.0502 0.000257214 6.20918 0.000257214 6.37495C0.000257214 6.54073 0.0640659 6.69971 0.177646 6.81693C0.291226 6.93415 0.445273 7 0.605899 7C0.766525 7 0.920572 6.93415 1.03415 6.81693L3.82236 3.93939C3.87867 3.88139 3.92334 3.8125 3.95383 3.73666C3.98431 3.66082 4 3.57952 4 3.49741C4 3.41531 3.98431 3.33401 3.95383 3.25817C3.92334 3.18233 3.87867 3.11343 3.82236 3.05544L1.03415 0.177895C0.803321 -0.060333 0.414552 -0.0603329 0.177646 0.184164Z"
                                    fill="#2B1C50"
                                />
                            </svg>
                        </button>
                    </div>

                    {/* Total amount  */}
                    <div className="mb-3 text-center">
                        <h5 className="text-extraSmall text-black-50">total</h5>
                        <p className="text-md uppercase font-bold text-black">
                            {userProfile.country.currency}{" "}
                            <span className="text-purple-color inline-block ml-1.5">
                                {" "}
                                {selectedYearEarnings
                                    .map((x) => x.netAmount)
                                    .reduce(
                                        (partial_sum, a) => partial_sum + a,
                                        0
                                    )
                                    .toFixed(2)}
                            </span>
                        </p>
                    </div>

                    {/* all month  */}
                    <div className="">
                        {totalEarnings.map((earn) => (
                            <div
                                key={earn.id}
                                className="py-[7px] pl-2 flex justify-between items-center text-md font-medium text-black-60 border-b border-black-10"
                            >
                                <h4 className="text-ebony-color capitalize">
                                    {earn.month}
                                </h4>
                                <p className="uppercase">MYR {earn.earn}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
